import { useState, useEffect, useCallback } from 'react';

// VAPID public key for push notifications (you would get this from your server)
// For demo purposes, we'll use a placeholder
const VAPID_PUBLIC_KEY = null; // Set this from your backend

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if notifications are supported
export function notificationsSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

// Get notification permission status
export function getNotificationPermission() {
  if (!notificationsSupported()) return 'unsupported';
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!notificationsSupported()) {
    return { granted: false, reason: 'unsupported' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { 
      granted: permission === 'granted',
      permission 
    };
  } catch (error) {
    console.error('Failed to request notification permission:', error);
    return { granted: false, reason: 'error', error };
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!notificationsSupported()) {
    return { success: false, reason: 'unsupported' };
  }
  
  if (Notification.permission !== 'granted') {
    return { success: false, reason: 'permission_denied' };
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      return { success: true, subscription, existing: true };
    }
    
    // Subscribe with VAPID key if available
    const subscribeOptions = {
      userVisibleOnly: true
    };
    
    if (VAPID_PUBLIC_KEY) {
      subscribeOptions.applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    }
    
    subscription = await registration.pushManager.subscribe(subscribeOptions);
    
    return { success: true, subscription, existing: false };
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    return { success: false, reason: 'error', error };
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      return { success: true };
    }
    
    return { success: true, wasSubscribed: false };
  } catch (error) {
    console.error('Failed to unsubscribe from push:', error);
    return { success: false, error };
  }
}

// Send a local notification (without push server)
export async function sendLocalNotification(title, options = {}) {
  if (!notificationsSupported()) {
    console.log('Notifications not supported');
    return false;
  }
  
  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      icon: '/icons/icon-192x192.svg',
      badge: '/icons/icon-96x96.svg',
      vibrate: [100, 50, 100],
      ...options
    });
    
    return true;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return false;
  }
}

// Send budget alert notification
export async function sendBudgetAlertNotification(category, percentage, language = 'fr') {
  const title = language === 'fr' ? '⚠️ Alerte Budget' : language === 'pt' ? '⚠️ Alerta de Orçamento' : '⚠️ Budget Alert';
  
  let body;
  if (percentage >= 100) {
    body = language === 'fr' 
      ? `Tu as dépassé ton budget ${category} !`
      : language === 'pt'
      ? `Excedeste o teu orçamento de ${category}!`
      : `You've exceeded your ${category} budget!`;
  } else {
    body = language === 'fr'
      ? `Tu as utilisé ${percentage}% de ton budget ${category}`
      : language === 'pt'
      ? `Usaste ${percentage}% do teu orçamento de ${category}`
      : `You've used ${percentage}% of your ${category} budget`;
  }
  
  return sendLocalNotification(title, {
    body,
    tag: 'budget-alert',
    data: { type: 'budget_warning', category, percentage },
    actions: [
      { action: 'view_budgets', title: language === 'fr' ? 'Voir budgets' : language === 'pt' ? 'Ver orçamentos' : 'View budgets' }
    ]
  });
}

// Send overdraft warning notification
export async function sendOverdraftNotification(daysUntil, amount, language = 'fr') {
  const title = language === 'fr' ? '🚨 Risque de Découvert' : language === 'pt' ? '🚨 Risco de Descoberto' : '🚨 Overdraft Risk';
  
  const body = language === 'fr'
    ? `Attention ! Découvert possible dans ${daysUntil} jour(s). Solde prévu: ${amount}€`
    : language === 'pt'
    ? `Atenção! Possível descoberto em ${daysUntil} dia(s). Saldo previsto: €${amount}`
    : `Warning! Possible overdraft in ${daysUntil} day(s). Expected balance: €${amount}`;
  
  return sendLocalNotification(title, {
    body,
    tag: 'overdraft-alert',
    requireInteraction: true,
    data: { type: 'overdraft_warning', daysUntil, amount },
    actions: [
      { action: 'view_dashboard', title: language === 'fr' ? 'Dashboard' : 'Dashboard' }
    ]
  });
}

// Send sync complete notification
export async function sendSyncCompleteNotification(count, language = 'fr') {
  const title = language === 'fr' ? '✅ Synchronisation' : language === 'pt' ? '✅ Sincronização' : '✅ Sync Complete';
  const body = language === 'fr'
    ? `${count} transaction(s) synchronisée(s)`
    : language === 'pt'
    ? `${count} transação(ões) sincronizada(s)`
    : `${count} transaction(s) synced`;
  
  return sendLocalNotification(title, {
    body,
    tag: 'sync-notification'
  });
}

// Send paused objectives reminder notification
export async function sendPausedObjectivesNotification(count, totalAmount, language = 'fr') {
  const title = language === 'fr' ? '⏸️ Rappel Objectifs' : language === 'pt' ? '⏸️ Lembrete de Objetivos' : '⏸️ Objectives Reminder';
  const body = language === 'fr'
    ? `${count} objectif(s) en pause avec ${totalAmount}€ d'économies. Souhaitez-vous les réactiver ?`
    : language === 'pt'
    ? `${count} objetivo(s) em pausa com €${totalAmount} poupados. Deseja reativá-los?`
    : `${count} objective(s) paused with €${totalAmount} saved. Would you like to reactivate them?`;
  
  return sendLocalNotification(title, {
    body,
    tag: 'paused-objectives-reminder',
    data: { type: 'paused_objectives', count, totalAmount },
    actions: [
      { action: 'view_objectives', title: language === 'fr' ? 'Voir objectifs' : language === 'pt' ? 'Ver objetivos' : 'View objectives' }
    ]
  });
}

// Check and send paused objectives notification (call this at app startup)
export async function checkAndNotifyPausedObjectives(pausedObjectives, language = 'fr', reminderDays = 5) {
  if (!pausedObjectives || pausedObjectives.length === 0) return false;
  
  // Only notify at the beginning of the month (days 1 to configured day)
  const today = new Date();
  if (today.getDate() > reminderDays) return false;
  
  // Check if we already notified this month
  const lastNotifiedKey = 'spenspot_paused_obj_notified';
  const lastNotified = localStorage.getItem(lastNotifiedKey);
  const currentMonth = `${today.getFullYear()}-${today.getMonth()}`;
  
  if (lastNotified === currentMonth) {
    return false; // Already notified this month
  }
  
  const totalAmount = pausedObjectives.reduce((sum, obj) => sum + (obj.current_amount || 0), 0);
  const sent = await sendPausedObjectivesNotification(pausedObjectives.length, totalAmount.toFixed(0), language);
  
  if (sent) {
    localStorage.setItem(lastNotifiedKey, currentMonth);
  }
  
  return sent;
}

// Custom hook for notifications
export function useNotifications() {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [isSubscribed, setIsSubscribed] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!notificationsSupported()) return;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  }, []);

  useEffect(() => {
    // Check subscription status on mount
    checkSubscription();
  }, [checkSubscription]);

  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(getNotificationPermission());
    return result;
  }, []);

  const subscribe = useCallback(async () => {
    const result = await subscribeToPush();
    if (result.success) {
      setIsSubscribed(true);
    }
    return result;
  }, []);

  const unsubscribe = useCallback(async () => {
    const result = await unsubscribeFromPush();
    if (result.success) {
      setIsSubscribed(false);
    }
    return result;
  }, []);

  return {
    supported: notificationsSupported(),
    permission,
    isSubscribed,
    requestPermission,
    subscribe,
    unsubscribe,
    sendLocalNotification,
    sendBudgetAlertNotification,
    sendOverdraftNotification
  };
}
